import { useState, useMemo } from 'react';
import { Loader2, Mail, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useEmailLogs } from '@/hooks/api/useNotifications';
import type { EmailLog, EmailLogsQuery } from '@/services/api/notifications.api';

// ─── Constants ───────────────────────────────────────────────────────────────

type EmailStatus = EmailLog['status'];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'failed', label: 'Failed' },
  { value: 'opened', label: 'Opened' },
];

const STATUS_STYLES: Record<EmailStatus, { bg: string; text: string; label: string }> = {
  queued: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Queued' },
  sent: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
  delivered: { bg: 'bg-green-100', text: 'text-green-700', label: 'Delivered' },
  bounced: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Bounced' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
  opened: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Opened' },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'MMM d, yyyy HH:mm');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function EmailLogsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const query: EmailLogsQuery = {
    page,
    limit: 25,
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(fromDate ? { from: fromDate } : {}),
    ...(toDate ? { to: toDate } : {}),
  };

  const { data, isLoading } = useEmailLogs(query);

  const allLogs: EmailLog[] = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;
  const totalCount = data?.meta?.total ?? 0;

  const logs = useMemo(() => {
    if (!searchTerm) return allLogs;
    const q = searchTerm.toLowerCase();
    return allLogs.filter(
      (log) =>
        log.recipientEmail.toLowerCase().includes(q) ||
        log.subject.toLowerCase().includes(q),
    );
  }, [allLogs, searchTerm]);

  const handleToggleRow = (id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  return (
    <div>
      <PageHeader
        title="Email Logs"
        description="Track email delivery status"
      />

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="email-log-status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="email-log-status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="email-log-from" className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              id="email-log-from"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="email-log-to" className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              id="email-log-to"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="email-log-search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              id="email-log-search"
              type="text"
              placeholder="Recipient or subject..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
            />
          </div>

          {(statusFilter || fromDate || toDate || searchTerm) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setStatusFilter('');
                setFromDate('');
                setToDate('');
                setSearchTerm('');
                setPage(1);
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading email logs" />
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={Mail}
          title="No email logs"
          description="No emails have been sent yet, or no results match your filters."
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Recipient</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Guest</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Template</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Subject</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">Sent At</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-600">
                    <span className="sr-only">Expand</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const style = STATUS_STYLES[log.status];
                  const isExpanded = expandedRowId === log.id;
                  const hasError = log.status === 'failed' && log.errorMessage;

                  return (
                    <LogRow
                      key={log.id}
                      log={log}
                      style={style}
                      isExpanded={isExpanded}
                      hasError={!!hasError}
                      onToggle={() => handleToggleRow(log.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {totalCount} total email{totalCount === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Log Row ────────────────────────────────────────────────────────────────

function LogRow({
  log,
  style,
  isExpanded,
  hasError,
  onToggle,
}: {
  log: EmailLog;
  style: { bg: string; text: string; label: string };
  isExpanded: boolean;
  hasError: boolean;
  onToggle: () => void;
}) {
  const ExpandIcon = isExpanded ? ChevronUp : ChevronDown;

  return (
    <>
      <tr
        className={cn(
          'border-b border-gray-100 hover:bg-gray-50 transition-colors',
          hasError && 'cursor-pointer',
        )}
        onClick={hasError ? onToggle : undefined}
      >
        <td className="px-4 py-3 text-gray-900">{log.recipientEmail}</td>
        <td className="px-4 py-3 text-gray-600">{log.guest?.fullName ?? '-'}</td>
        <td className="px-4 py-3 text-gray-600 capitalize">{log.templateType.replace(/_/g, ' ')}</td>
        <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{log.subject}</td>
        <td className="px-4 py-3">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              style.bg,
              style.text,
            )}
          >
            {style.label}
          </span>
        </td>
        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(log.sentAt)}</td>
        <td className="px-4 py-3">
          {hasError && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse error details' : 'Expand error details'}
            >
              <ExpandIcon size={16} className="text-gray-400" aria-hidden="true" />
            </button>
          )}
        </td>
      </tr>
      {isExpanded && hasError && (
        <tr className="border-b border-gray-100 bg-red-50">
          <td colSpan={7} className="px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-red-700">Error Details</p>
                <p className="text-sm text-red-600 mt-1">{log.errorMessage}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
